// Shared catalog form primitives — extracted from Catalog.tsx so the
// Monetization page (subscription products / credit packs / signup
// bonuses, PX-149) reuses the exact same inline-edit + create-form
// controls. One definition → one place to restyle every admin table.

import { useEffect, useRef, useState } from 'react'
import { APIError } from '@/lib/api'

// ----- Create-form controls -----------------------------------------

export function Field({
  label, value, onChange, mono, type = 'text', placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  mono?: boolean
  type?: 'text' | 'number'
  placeholder?: string
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-ink-500">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-md border border-ink-200 px-3 py-2 ${mono ? 'font-mono text-xs' : 'text-sm'} focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100`}
      />
    </label>
  )
}

export function Checkbox({
  label, checked, onChange,
}: {
  label: string; checked: boolean; onChange: (v: boolean) => void
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-ink-200 text-brand-500 focus:ring-brand-200"
      />
      <span className="text-ink-700">{label}</span>
    </label>
  )
}

// ----- Inline table cells -------------------------------------------

export function Toggle({
  checked, onChange, disabled,
}: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean
}) {
  return (
    <span
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full transition ${checked ? 'bg-brand-500' : 'bg-ink-200'} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${checked ? 'left-[18px]' : 'left-0.5'}`} />
    </span>
  )
}

// Grip that initiates an HTML5 drag carrying the row id. Only the handle is
// `draggable`, so the row's inputs/links stay editable (a draggable row would
// hijack text selection). Shared by the photoshoot variable/option rows
// (PX-313) and the media-carousel rows (PX-330).
export function DragHandle({ id }: { id: string }) {
  return (
    <span
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', id); e.dataTransfer.effectAllowed = 'move' }}
      title="Drag to reorder"
      aria-label="Drag to reorder"
      className="select-none cursor-grab text-ink-300 transition hover:text-ink-500 active:cursor-grabbing"
    >
      ⠿
    </span>
  )
}

// Inline cell editors — local draft state, commit on blur or Enter,
// revert on Escape or blur-without-change. PATCH only fires when the
// committed value actually differs from the server value, so re-renders
// from query invalidation don't trigger spurious mutations.
//
// `allowEmpty` lets a nullable column (e.g. entitlement_id) be cleared:
// committing an empty string calls `onCommit('')` and the caller maps it
// to null. Without it (the default) an empty edit reverts to the prior
// value — the right behaviour for required columns like a name.
export function InlineTextCell({
  value, onCommit, disabled, allowEmpty, placeholder, mono,
}: {
  value: string
  onCommit: (next: string) => void
  disabled?: boolean
  allowEmpty?: boolean
  placeholder?: string
  mono?: boolean
}) {
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDraft(value) }, [value])

  const commit = () => {
    const trimmed = draft.trim()
    if (!trimmed && !allowEmpty) { setDraft(value); return }
    if (trimmed === value) return
    onCommit(trimmed)
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur() }
        if (e.key === 'Escape') { setDraft(value); inputRef.current?.blur() }
      }}
      className={`w-full min-w-[7rem] rounded border border-transparent bg-transparent px-2 py-1 ${mono ? 'font-mono text-xs' : 'text-sm'} focus:border-ink-200 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-100 disabled:cursor-not-allowed disabled:opacity-60`}
    />
  )
}

export function InlineNumberCell({
  value, onCommit, disabled, min, max, widthClass = 'w-16',
}: {
  value: number
  onCommit: (next: number) => void
  disabled?: boolean
  min?: number
  max?: number
  widthClass?: string
}) {
  const [draft, setDraft] = useState(String(value))
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDraft(String(value)) }, [value])

  const commit = () => {
    if (draft.trim() === '') { setDraft(String(value)); return }
    const n = Number(draft)
    if (!Number.isFinite(n) || !Number.isInteger(n)) { setDraft(String(value)); return }
    if (min !== undefined && n < min) { setDraft(String(value)); return }
    if (max !== undefined && n > max) { setDraft(String(value)); return }
    if (n === value) return
    onCommit(n)
  }

  return (
    <input
      ref={inputRef}
      type="number"
      step={1}
      min={min}
      max={max}
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur() }
        if (e.key === 'Escape') { setDraft(String(value)); inputRef.current?.blur() }
      }}
      className={`${widthClass} rounded border border-transparent bg-transparent px-2 py-1 text-right tabular-nums focus:border-ink-200 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-100 disabled:cursor-not-allowed disabled:opacity-60`}
    />
  )
}

// ----- Error surface -------------------------------------------------

// Renders an APIError's human message — UNIQUE collisions (slug /
// revenuecat_product_id already exists) arrive here as a clean 409
// message from the backend envelope, so the operator sees exactly what
// clashed instead of a generic failure.
export function ErrorBanner({ error }: { error: APIError }) {
  return (
    <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {error.message}
    </div>
  )
}

// Convenience: pick the first APIError out of a set of mutations'
// errors. Returns null when none is an APIError (e.g. a network blow-up
// surfaces through react-query's own retry/boundary instead).
export function firstApiError(...errors: unknown[]): APIError | null {
  for (const e of errors) {
    if (e instanceof APIError) return e
  }
  return null
}

// ----- Shared table / form chrome -----------------------------------

// The right-aligned "New <thing>" primary button that opens a create
// form. One definition across every catalog list (plans / models /
// products / packs).
export function NewItemButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="mb-3 flex justify-end">
      <button
        type="button"
        onClick={onClick}
        className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600"
      >
        {label}
      </button>
    </div>
  )
}

// Cancel / submit row at the foot of every create form.
export function FormActions({
  onCancel, submitting, submitLabel = 'Create',
}: {
  onCancel: () => void; submitting: boolean; submitLabel?: string
}) {
  return (
    <div className="mt-3 flex justify-end gap-2">
      <button type="button" onClick={onCancel} className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300">
        Cancel
      </button>
      <button type="submit" disabled={submitting} className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60">
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  )
}

// Row delete button — browser confirm() gate (matches the rest of the
// admin SPA; the iOS `.alert` rule is iOS-only). Server-side the delete
// is refused with a clean 409 if the row is still referenced, surfaced
// through the table's ErrorBanner.
export function DeleteButton({
  confirmText, onConfirm, disabled,
}: {
  confirmText: string; onConfirm: () => void; disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => { if (confirm(confirmText)) onConfirm() }}
      className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      Delete
    </button>
  )
}

// Segmented tab switcher (Apple-ish pill group). Generic over the tab id
// so Catalog (plans/models) and Monetization (products/packs/bonuses)
// share one control. The parent decides which tabs to pass — e.g.
// Catalog filters by permission before handing them in.
export function TabSwitcher<T extends string>({
  tabs, current, onChange,
}: {
  tabs: readonly { id: T; label: string }[]
  current: T
  onChange: (id: T) => void
}) {
  return (
    <div className="mb-4 inline-flex gap-1 rounded-lg bg-ink-100 p-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
            current === t.id ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}
